import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import type { Portfolio, Fund } from 'venn-api';
import type { SearchMenuItem } from '../../search-menu';

export type SearchView = 'CONSTITUENT_SELECTION' | 'INVESTMENT_SEARCH' | 'STRATEGY_ASSIGNMENT' | 'ADD_TAX_INFORMATION';

interface AllocationSearchContextValue {
  view: SearchView;
  selectedConstituent?: Portfolio;
  selectedFundForStrategyAssignment?: Fund;
  selectedInvestmentId?: string;
  setView: (view: SearchView) => void;
  setSelectedConstituent: (constituent: Portfolio | undefined) => void;
  setSelectedFundForStrategyAssignment: (fund: Fund | undefined) => void;
  handleConstituentSelect: (constituent: Portfolio) => void;
  handleBack: () => void;
  handleSingleSelectFund: (item: SearchMenuItem) => void;
  handleStrategyAssignment: (fund: Fund) => void;
  handleStrategySelected: (strategyId: string, strategyName: string) => void;
  handleAddTaxInformation: () => void;
}

const AllocationSearchContext = createContext<AllocationSearchContextValue | undefined>(undefined);
AllocationSearchContext.displayName = 'AllocationSearchContext';

export interface FundSelectionResult {
  needsManualStrategyAssignment?: boolean;
}

interface AllocationSearchProviderProps {
  children: React.ReactNode;
  initialView: SearchView;
  onSingleSelectFund: (
    item: SearchMenuItem,
    constituentId?: number,
  ) => Promise<FundSelectionResult | void> | FundSelectionResult | void;
  onStrategyAssignment?: (fund: Fund, strategyId: string, constituentId?: number) => void;
  hasTaxInformation?: boolean;
}

export const AllocationSearchProvider: React.FC<AllocationSearchProviderProps> = ({
  children,
  initialView,
  onSingleSelectFund,
  onStrategyAssignment,
  hasTaxInformation = false,
}) => {
  const [view, setView] = useState<SearchView>(initialView);
  const [selectedConstituent, setSelectedConstituent] = useState<Portfolio | undefined>();
  const [selectedFundForStrategyAssignment, setSelectedFundForStrategyAssignment] = useState<Fund | undefined>();
  const [selectedInvestmentId, setSelectedInvestmentId] = useState<string | undefined>();

  const handleConstituentSelect = useCallback((constituent: Portfolio) => {
    setSelectedConstituent(constituent);
    setView('INVESTMENT_SEARCH');
  }, []);

  const handleBack = useCallback(() => {
    setSelectedConstituent(undefined);
    setSelectedFundForStrategyAssignment(undefined);
    setView('CONSTITUENT_SELECTION');
  }, []);

  const handleStrategyAssignment = useCallback((fund: Fund) => {
    setSelectedFundForStrategyAssignment(fund);
    setView('STRATEGY_ASSIGNMENT');
  }, []);

  const handleAddTaxInformation = useCallback(() => {
    setView('ADD_TAX_INFORMATION');
  }, []);

  const handleSingleSelectFund = useCallback(
    async (item: SearchMenuItem) => {
      const fund = item.value?.fund;
      if (!fund) {
        return;
      }

      const result = await onSingleSelectFund(item, selectedConstituent?.id);

      if (result && result.needsManualStrategyAssignment) {
        handleStrategyAssignment(fund);
      } else if (hasTaxInformation) {
        setSelectedInvestmentId(fund.id);
        handleAddTaxInformation();
      }
    },
    [selectedConstituent, onSingleSelectFund, handleStrategyAssignment, hasTaxInformation, handleAddTaxInformation],
  );

  const handleStrategySelected = useCallback(
    (strategyId: string, _strategyName: string) => {
      if (selectedFundForStrategyAssignment && onStrategyAssignment) {
        onStrategyAssignment(selectedFundForStrategyAssignment, strategyId, selectedConstituent?.id);
        setSelectedInvestmentId(selectedFundForStrategyAssignment?.id);
        setSelectedFundForStrategyAssignment(undefined);
      }
      if (hasTaxInformation) {
        handleAddTaxInformation();
      }
    },
    [
      selectedFundForStrategyAssignment,
      selectedConstituent,
      onStrategyAssignment,
      hasTaxInformation,
      handleAddTaxInformation,
    ],
  );

  const value: AllocationSearchContextValue = useMemo(
    () => ({
      view,
      selectedConstituent,
      selectedFundForStrategyAssignment,
      selectedInvestmentId,
      setView,
      setSelectedConstituent,
      setSelectedFundForStrategyAssignment,
      handleConstituentSelect,
      handleBack,
      handleSingleSelectFund,
      handleStrategyAssignment,
      handleAddTaxInformation,
      handleStrategySelected,
    }),
    [
      view,
      selectedConstituent,
      selectedFundForStrategyAssignment,
      selectedInvestmentId,
      handleConstituentSelect,
      handleBack,
      handleSingleSelectFund,
      handleStrategyAssignment,
      handleAddTaxInformation,
      handleStrategySelected,
    ],
  );

  return <AllocationSearchContext.Provider value={value}>{children}</AllocationSearchContext.Provider>;
};

export const useAllocationSearch = () => {
  const context = useContext(AllocationSearchContext);
  if (!context) {
    throw new Error('useAllocationSearch must be used within AllocationSearchProvider');
  }
  return context;
};
